import { useUpdatingProxyNiceModal } from '@lessonup/client-integration';
import { FontLoader, PinViewerStatic } from '@lessonup/pin-renderer';
import {
  createPlayerStoreProxy,
  PlayerBarBottomPlaceholder,
  SimplePlayerStoreContextProvider,
  useCurrentPin,
  useCurrentStudents,
  useGetPinThemeById,
  useIsPlayerLoading,
  useNonNullAssignment,
  usePlayerDispatcher,
  usePlayerStore,
  useCurrentPinAdjacentRawPinSlices,
} from '@lessonup/players-modern';
import { color, SpaceBetween, spacing, styled } from '@lessonup/ui-components';
import React from 'react';
import { RealtimePlayerBarBottomHandler } from '../dataComponents/PlayerBarBottomHandler/RealtimePlayerBarBottomHandler';
import { usePlayerTranslation } from '../hooks/usePlayerTranslation';
import { PinViewerRealtimePlayer } from './dataComponents/PinViewerRealtimePlayer/PinViewerRealtimePlayer';
import { StudentJoinScreenModal } from './dataComponents/playerScreens/StudentJoinScreen/StudentJoinScreenModal';
import { useRealtimePlayerKeyboardEvents } from './hooks/useRealtimePlayerKeyboardEvents';
import { changeAllowNewPlayersAction, removeStudentFromAssignmentAction } from './utils/playerDispatcher.utils';

export interface RealtimePlayerProps {
  onClose: () => void;
  withPresenterMode: boolean;
}

export const InitializedRealtimePlayer: React.FC<RealtimePlayerProps> = ({ onClose, withPresenterMode }) => {
  const { t } = usePlayerTranslation();
  const assignment = useNonNullAssignment();
  const currentPin = useCurrentPin();
  const getPinThemeById = useGetPinThemeById();
  const currentPinTheme = getPinThemeById(currentPin?.id);
  const currentStudents = useCurrentStudents();
  const { setPlayerNavigationLock, closePlayer } = usePlayerStore();
  const dispatch = usePlayerDispatcher();
  const { previousPins, nextPins } = useCurrentPinAdjacentRawPinSlices(2);

  useRealtimePlayerKeyboardEvents(onClose);

  const handleClose = () => {
    closePlayer();
    onClose();
  };

  const handleAllowNewPlayers = async (allowNewPlayers: boolean) =>
    dispatch(changeAllowNewPlayersAction(allowNewPlayers));
  const handleRemoveStudent = async (studentId: string) => dispatch(removeStudentFromAssignmentAction(studentId));

  const allowNewPlayers = assignment.settings.status === 'open' && assignment.settings.playerSettings.allowNewPlayers; // Is this correct?
  const pincode = assignment.pincode ?? '';

  const studentModal = useUpdatingProxyNiceModal(
    StudentJoinScreenModal,
    {
      joinedStudents: currentStudents,
      pincode,
      allowNewPlayers,
      handleAllowNewPlayers,
      handleRemoveStudent,
      onClose: () => {
        setPlayerNavigationLock(false);
      },
      t,
    },
    [currentStudents, allowNewPlayers]
  );

  const handleShowJoinScreenModal = () => {
    setPlayerNavigationLock(true);
    studentModal.show();
  };

  return (
    <StyledFullscreenPlayerWrapper>
      <FontLoader />
      <StyledRealtimePlayerWrapper spacing={spacing.size8} direction="y">
        <StyledPinViewerWrapper>
          {/* Loading prev|next pins to preload their static content. TODO virtualize these */}
          {previousPins.map((pin) => (
            <InvisiblePreview key={pin.id} pin={pin} pinTheme={getPinThemeById(pin.id)} />
          ))}
          {currentPin && <PinViewerRealtimePlayer pin={currentPin} withFAM pinTheme={currentPinTheme} />}
          {nextPins.map((pin) => (
            <InvisiblePreview key={pin.id} pin={pin} pinTheme={getPinThemeById(pin.id)} />
          ))}
        </StyledPinViewerWrapper>
        <RealtimePlayerBarBottomHandler
          showLabels={true}
          handleShowJoinScreenModal={handleShowJoinScreenModal}
          onClose={handleClose}
          withPresenterMode={withPresenterMode}
          // TODO show label based on player settings
        />
      </StyledRealtimePlayerWrapper>
    </StyledFullscreenPlayerWrapper>
  );
};

export const PlayerSkeleton: React.FC = () => {
  const playerStoreProxy = createPlayerStoreProxy();
  return (
    <SimplePlayerStoreContextProvider value={playerStoreProxy}>
      <StyledFullscreenPlayerWrapper>
        <StyledRealtimePlayerWrapper spacing={spacing.size8} direction="y">
          <StyledPinViewerWrapper />
          <PlayerBarBottomPlaceholder minimized={true} mirrored={false} />
        </StyledRealtimePlayerWrapper>
      </StyledFullscreenPlayerWrapper>
    </SimplePlayerStoreContextProvider>
  );
};

export const RealtimePlayer: React.FC<RealtimePlayerProps> = ({ onClose, withPresenterMode }) => {
  const loading = useIsPlayerLoading();

  if (loading) {
    return <PlayerSkeleton />;
  }

  return <InitializedRealtimePlayer onClose={onClose} withPresenterMode={withPresenterMode} />;
};

/**
 * @description This wrapper is required for fullscreen to maintain a white background (like the site body has) due to our colors being rgba.
 */
const StyledFullscreenPlayerWrapper = styled.div`
  background: ${color.neutral.surface.background};
`;

const StyledRealtimePlayerWrapper = styled(SpaceBetween)`
  height: 100vh;
  padding: ${spacing.size8};
  display: flex;
  flex-direction: column;
  background: ${color.neutralNew.black.fill};
  overflow: hidden;
`;

// The default of flex items is min-width: auto and min-height: auto.
// The resize observer in the PinPreview component will report the wrong bounding box with resize auto
const StyledPinViewerWrapper = styled.div`
  flex: 1;
  min-width: 0; //⬆
  min-height: 0; //⬆
`;

const InvisiblePreview = styled(PinViewerStatic)`
  display: none;
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
`;
